// Buscador rápido de personas — usado dentro del módulo Personal.
// Estilo claro (no traslúcido como el header global).

import React, { useState, useEffect, useRef } from 'react';
import {
    Autocomplete, TextField, InputAdornment, Box, Avatar, Typography, Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { personalBuscarRapidoJWT } from '../../api/endpoints';
import { nodeApiPath } from '../../utils/paths';

export default function PersonalQuickSearch({ onSelect, variant = 'light' }) {
    const isDark = variant === 'dark';
    const [q, setQ] = useState('');
    const [opts, setOpts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);
    const reqRef = useRef(0);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!q || q.trim().length < 2) { setOpts([]); return; }
        setLoading(true);
        const reqId = ++reqRef.current;
        debounceRef.current = setTimeout(async () => {
            try {
                const r = await personalBuscarRapidoJWT(q.trim(), 12);
                if (reqId !== reqRef.current) return;
                setOpts(r?.ok ? (r.items || []) : []);
            } catch {
                if (reqId === reqRef.current) setOpts([]);
            } finally {
                if (reqId === reqRef.current) setLoading(false);
            }
        }, 280);
        return () => clearTimeout(debounceRef.current);
    }, [q]);

    const handleSelect = (_e, value) => {
        if (!value || typeof value === 'string') return;
        setQ(''); setOpts([]); setOpen(false);
        onSelect?.(value);
    };

    return (
        <Autocomplete
            size="small"
            freeSolo
            open={open && opts.length > 0}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={opts}
            loading={loading}
            filterOptions={(x) => x}
            getOptionLabel={(o) => typeof o === 'string' ? o : `${o.Apellido || ''}, ${o.Nombre || ''}`}
            onChange={handleSelect}
            onInputChange={(_e, v, reason) => { if (reason === 'input') setQ(v); }}
            noOptionsText={q.length < 2 ? 'Escribí al menos 2 caracteres' : 'Sin resultados'}
            loadingText="Buscando..."
            renderOption={(props, opt) => (
                <li {...props} key={opt.Id}>
                    <Avatar
                        src={`${nodeApiPath(`personal/${opt.Id}/foto`)}?token=${localStorage.getItem('SIGIF_JWT_TOKEN')}`}
                        sx={{ width: 32, height: 32, mr: 1.5, fontSize: 13, bgcolor: opt.Activo ? '#0277bd' : 'grey.500' }}
                    >
                        {(opt.Apellido?.[0] || '?').toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {opt.Apellido}, {opt.Nombre}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {opt.Funcion || '—'} · {opt.Area || '—'}
                        </Typography>
                    </Box>
                    {!opt.Activo && (
                        <Chip label="Inactivo" size="small" sx={{ ml: 1, height: 20, fontSize: 10 }} />
                    )}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Buscar persona por nombre, DNI, CUIL o email..."
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: isDark ? 'rgba(255,255,255,0.9)' : '#0277bd' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={isDark ? {
                        '& .MuiOutlinedInput-root': {
                            background: 'rgba(255,255,255,0.15)',
                            color: '#fff',
                            borderRadius: '4px',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                            '&.Mui-focused fieldset': { borderColor: '#fff' },
                        },
                        '& input::placeholder': { color: 'rgba(255,255,255,0.85)', opacity: 1 },
                        '& input': { color: '#fff' },
                    } : {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                            '& fieldset': { borderColor: '#0277bd44' },
                            '&:hover fieldset': { borderColor: '#0277bd' },
                            '&.Mui-focused fieldset': { borderColor: '#0277bd' },
                        },
                    }}
                />
            )}
        />
    );
}
